import { IAssignment } from '@/components/staffer/AssignmentsBasedGradesTable/types';
import { ISubmission } from '@/components/interfaces';
import cn from '@/utils/cn';

export const TABLE_ROW_CLASS = 'tw-text-start tw-w-[14%]';
export const TABLE_HEADER_CLASS = cn(TABLE_ROW_CLASS, 'tw-pb-4');
export const TABLE_ROW_WITH_BORDER_CLASS = cn(TABLE_ROW_CLASS, 'tw-p-4 tw-border-t tw-border-med-grey tw-leading-5',
  'tw-border-solid tw-align-middle');

export type StudentRowDataType = {
  assignment_details: {
    type: string,
    title: string,
  },
  assignment_id: number,
  due_datetime: string,
  max_points: string,
  status: string,
  submission_id: number | null,
  submitted_at?: string | null,
  total_score?: number | null,
  total_score_override?: number | null,
};

// Returns the value at the end of the path if it exists
// Helper: safely get nested value from object by dot-path
export const getValueByPath = (
  obj: any,
  path: string,
) => path.split('.').reduce((acc, key) => acc?.[key], obj);

// Helper: status sorting logic
const getStatusOrder = (row: StudentRowDataType) => {
  // 'to_do' but has no submission_id means not started
  if (row.status === 'to_do' && !row.submission_id) return 1;
  if (row.status === 'to_do') return 2;
  if (row.status === 'completed') return 3;
  return -1;
};

const compareValues = (a: any, b: any, order: 'asc' | 'desc' = 'asc') => {
  // Place null/undefined values at the end
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (a < b) return order === 'asc' ? -1 : 1;
  if (a > b) return order === 'asc' ? 1 : -1;
  return 0;
};

export const sortTableData = (
  path: string,
  data: StudentRowDataType[] = [],
  order: 'asc' | 'desc' = 'asc',
): StudentRowDataType[] => [...data].sort((a, b) => {
  if (path === 'status') {
    return compareValues(getStatusOrder(a), getStatusOrder(b), order);
  }

  if (path === 'total_score') {
    return compareValues(getValueByPath(a, 'total_score_override') ?? getValueByPath(a, path),
      getValueByPath(b, 'total_score_override') ?? getValueByPath(b, path), order);
  }

  const valueA = getValueByPath(a, path);
  const valueB = getValueByPath(b, path);
  return compareValues(valueA, valueB, order);
});

export interface IStudentAssignment extends IAssignment {
  status?: string,
}

export const mergeTableData = (
  assignments : IStudentAssignment[],
  submissions : Partial<ISubmission>[],
) => assignments.map((assignment) => {
  const submission = submissions.find(sub => sub.assignment_id === assignment.id);
  return {
    assignment_details: { ...assignment.assignment_details },
    assignment_id: assignment.id,
    due_datetime: assignment.due_datetime,
    max_points: assignment.max_points,
    status: assignment.status || 'not_submitted',
    submission_id: submission?.id ?? null,
    submitted_at: submission?.submitted_at ?? null,
    total_score: submission?.total_score ?? null,
    total_score_override: submission?.total_score_override ?? null,
  };
});

